import { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ChevronDown, ChevronRight, Eye, EyeOff } from "lucide-react";
import { BLOCK_SCHEMAS, BLOCK_TYPES } from "@/components/blocks/registry";
import BlockRenderer from "@/components/blocks/BlockRenderer";

// In-admin help page. Iterates every block type registered in
// `BLOCK_SCHEMAS`, showing its label, description, editable fields, and a live
// preview rendered with the schema defaults. New blocks appear here
// automatically — no separate documentation to update.

function FieldRow({ f }) {
  return (
    <tr className="border-b border-slate-100 last:border-0">
      <td className="py-2 pr-3 font-mono text-[11px] text-slate-500 whitespace-nowrap align-top">
        {f.key}
      </td>
      <td className="py-2 pr-3 text-[12px] text-slate-700 align-top">{f.label}</td>
      <td className="py-2 text-[11px] font-mono text-sky-600 whitespace-nowrap align-top">
        {f.type}
        {f.type === "object_list" && f.item_fields ? (
          <span className="text-slate-400"> ({f.item_fields.length})</span>
        ) : null}
      </td>
    </tr>
  );
}

function ObjectListSubFields({ f }) {
  if (!f.item_fields?.length) return null;
  return (
    <div className="ml-6 mt-1 pl-3 border-l-2 border-sky-100">
      <p className="text-[10px] uppercase font-bold tracking-widest text-slate-400 mb-1">
        Fields inside each item
      </p>
      <table className="w-full text-left">
        <tbody>
          {f.item_fields.map((sub) => (
            <FieldRow key={sub.key} f={sub} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BlockCard({ type }) {
  const schema = BLOCK_SCHEMAS[type];
  const [showPreview, setShowPreview] = useState(false);
  const [showFields, setShowFields] = useState(false);

  // Build a "demo" block with schema defaults. Some widget blocks have {}
  // defaults; those still render via their existing site-settings-backed
  // component, so previews stay authentic.
  const demoBlock = { id: `preview-${type}`, type, data: schema.defaults || {} };

  return (
    <div
      className="border border-slate-200 rounded-xl bg-white overflow-hidden"
      data-testid={`block-guide-card-${type}`}
    >
      <div className="p-5 border-b border-slate-100 bg-slate-50/40">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-[10px] uppercase font-bold tracking-widest text-sky-700 bg-sky-50 border border-sky-100 rounded-full px-2 py-0.5 font-mono">
            {type}
          </span>
          <h3 className="text-base font-bold text-slate-900">{schema.label}</h3>
        </div>
        {schema.description && (
          <p className="text-[13px] text-slate-600 leading-relaxed max-w-3xl">
            {schema.description}
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowPreview((v) => !v)}
            data-testid={`block-guide-toggle-preview-${type}`}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-700 hover:border-sky-300 hover:text-sky-700 transition"
          >
            {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {showPreview ? "Hide preview" : "Show preview"}
          </button>
          <button
            type="button"
            onClick={() => setShowFields((v) => !v)}
            data-testid={`block-guide-toggle-fields-${type}`}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-700 hover:border-sky-300 hover:text-sky-700 transition"
          >
            {showFields ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Editable fields ({schema.fields?.length || 0})
          </button>
        </div>
      </div>

      {showFields && (
        <div className="p-5 border-b border-slate-100 bg-white">
          {schema.fields?.length ? (
            <>
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b-2 border-slate-200">
                    <th className="pb-2 pr-3 text-[10px] uppercase tracking-widest text-slate-400 font-mono">Field</th>
                    <th className="pb-2 pr-3 text-[10px] uppercase tracking-widest text-slate-400">Label shown in editor</th>
                    <th className="pb-2 text-[10px] uppercase tracking-widest text-slate-400 font-mono">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {schema.fields.map((f) => (
                    <FieldRow key={f.key} f={f} />
                  ))}
                </tbody>
              </table>
              {schema.fields
                .filter((f) => f.type === "object_list")
                .map((f) => (
                  <ObjectListSubFields key={`sub-${f.key}`} f={f} />
                ))}
            </>
          ) : (
            <p className="text-[13px] text-slate-500 italic">
              This block has no editable fields inside the block editor. Its content is managed
              elsewhere (usually Site Settings). Use it to reorder or hide the section only.
            </p>
          )}
        </div>
      )}

      {showPreview && (
        <div
          className="relative bg-white"
          data-testid={`block-guide-preview-${type}`}
        >
          <div className="absolute top-3 left-3 z-10 pointer-events-none">
            <span className="inline-block text-[10px] uppercase font-bold tracking-widest text-slate-500 bg-white/90 border border-slate-200 rounded-full px-2 py-0.5 shadow-sm">
              Live preview
            </span>
          </div>
          <div className="border-t border-slate-200">
            <BlockRenderer block={demoBlock} index={0} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminBlockLibraryPage() {
  return (
    <div className="bg-slate-50 min-h-full">
      <div className="p-5 lg:p-8 max-w-5xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
            <BookOpen className="h-5 w-5 text-sky-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Block Library</h1>
            <p className="text-xs text-slate-500">
              Every block available in the Site Pages editor, with a live preview and full field list.
            </p>
          </div>
        </div>

        <div className="bg-sky-50 border border-sky-100 rounded-lg p-4 mb-6 text-[13px] text-sky-900">
          <p className="font-semibold mb-1">Tip</p>
          <p className="leading-relaxed">
            To use one of these blocks, go to{" "}
            <Link to="/admin/site-pages" className="underline font-semibold">
              Site Pages
            </Link>
            , open any page, and click <em>Add a block</em> at the bottom. The block name in the
            list below matches the button label in the editor.
          </p>
        </div>

        <div className="space-y-4" data-testid="block-guide-list">
          {BLOCK_TYPES.map((type) => (
            <BlockCard key={type} type={type} />
          ))}
        </div>
      </div>
    </div>
  );
}
